import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Autocomplete from "react-google-autocomplete";
import { CongratulationsDialog } from "app/pages/Customer/FixedPhone/components/congratulations-dialog";
import { ErrorDialog } from "app/pages/Customer/FixedPhone/components/error-dialog";
import {
  useLocationUtils,
  validateAddressInput,
  validateZipCode,
  checkServiceAvailability,
  isServiceAvailable,
} from "utils/location-utils";
import { ActivationEntryType } from "app/pages/Customer/Activation/activation-entry-types";
import { saveActivationFlowData } from "app/pages/Customer/FixedPhone/Checkout/utils/fixed-phone-storage";

import { Label } from "app/components/ui/label";
import { Button } from "app/components/ui/button";
import { Icons } from "app/components/ui/icons";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "app/components/ui/dialog";

interface Props {
  showDialog: boolean;
  setShowDialog: React.Dispatch<React.SetStateAction<boolean>>;
  onContinue: () => void;
  startWithCongratulations?: boolean;
  initialAddress?: string;
}

export const CheckCoverageDialog = ({
  showDialog,
  setShowDialog,
  onContinue,
  startWithCongratulations,
  initialAddress,
}: Props) => {
  const navigate = useNavigate();
  const handleNavigateToFixedPhoneSimService = useCallback(() => {
    sessionStorage.setItem("simServiceActiveTab", "fixedPhone");
    navigate("/my-account/sim-service", {
      state: {
        activeTab: "fixedPhone",
        highlightDetails: true,
      },
    });
  }, [navigate]);

  const [isLoading, setIsLoading] = useState(false);
  const [isAutocompleteOpen, setIsAutocompleteOpen] = useState(false);
  const [showCongratulationsDialog, setShowCongratulationsDialog] =
    useState(false);
  const [showErrorDialog, setShowErrorDialog] = useState(false);

  // Use the location utility hook
  const {
    address,
    zipCode,
    addressError,
    setAddress,
    setAddressError,
    handleAddressSelect,
  } = useLocationUtils();

  // Monitor Google Maps autocomplete dropdown visibility
  useEffect(() => {
    const checkDropdownVisibility = () => {
      const pacContainer = document.querySelector(
        ".pac-container",
      ) as HTMLElement;
      if (pacContainer) {
        const isVisible =
          pacContainer.style.display !== "none" &&
          pacContainer.style.visibility !== "hidden" &&
          pacContainer.offsetParent !== null;
        setIsAutocompleteOpen(isVisible);
      }
    };

    // Check periodically for dropdown visibility
    const interval = setInterval(checkDropdownVisibility, 100);

    // Also listen for DOM changes
    const observer = new MutationObserver(checkDropdownVisibility);
    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      clearInterval(interval);
      observer.disconnect();
    };
  }, []);

  // Handle initial congratulations restore
  useEffect(() => {
    if (showDialog && startWithCongratulations) {
      if (initialAddress) {
        setAddress(initialAddress);
      }
      setShowCongratulationsDialog(true);
    }
  }, [showDialog, startWithCongratulations, initialAddress, setAddress]);

  const handleCheckAvailability = async () => {
    // Clear previous errors
    setAddressError("");

    // Validate address input using utility function
    const validationError = validateAddressInput(address, zipCode);
    if (validationError) {
      setAddressError(validationError);
      return;
    }

    // Additional zip code validation before API call
    if (!validateZipCode(zipCode)) {
      setAddressError("Please enter a valid 5-digit zip code");
      return;
    }

    setIsLoading(true);
    try {
      const data = await checkServiceAvailability(zipCode);

      if (isServiceAvailable(data)) {
        saveActivationFlowData(
          {
            zipCode,
            address,
          },
          { activationPurchaseFlow: ActivationEntryType.FixedPhoneBase }
        );
        setShowDialog(false);
        setShowCongratulationsDialog(true);
      } else {
        setShowDialog(false);
        setShowErrorDialog(true);
      }
    } catch (error) {
      console.error("Error checking availability:", error);
      setShowDialog(false);
      setShowErrorDialog(true);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <React.Fragment>
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent
          className="bg-white w-[95%] sm:max-w-[33.5rem] p-4 sm:p-5 lg:p-6 gap-3.5 sm:gap-4 lg:gap-5"
          onPointerDownOutside={(e) => {
            // Prevent dialog from closing when clicking on Google Maps autocomplete dropdown
            const target = e.target as Element;
            if (target.closest(".pac-container") || isAutocompleteOpen) {
              e.preventDefault();
            }
          }}
          onInteractOutside={(e) => {
            // Prevent dialog from closing when clicking on Google Maps autocomplete dropdown
            const target = e.target as Element;
            if (target.closest(".pac-container") || isAutocompleteOpen) {
              e.preventDefault();
            }
          }}
        >
          <DialogHeader className="flex-row justify-between items-center p-0 bg-transparent">
            <DialogTitle className="text-xl font-bold leading-5 text-black sm:leading-6 md:leading-7 lg:text-2xl font-mobile">
              Check Coverage
            </DialogTitle>
            <DialogClose className="shrink-0 rounded-full">
              <img
                src="/assets/close-circle.webp"
                className="shrink-0 size-5 rounded-full opacity-90 md:size-6 hover:opacity-100"
                alt="Close"
              />
            </DialogClose>
          </DialogHeader>
          <div className="">
            <Label
              htmlFor="address"
              className="w-fit text-black text-xs md:text-sm font-medium leading-4 md:leading-5 -mt-2 sm:mt-0 mb-1.5 sm:mb-2 md:mb-[0.7rem]"
            >
              Enter the address where you'll use this service.
            </Label>
            <Autocomplete
              apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}
              onPlaceSelected={(place) => {
                handleAddressSelect(place);
                setIsAutocompleteOpen(false);
              }}
              options={{
                types: ["address"],
                componentRestrictions: { country: "us" },
              }}
              className={`w-full h-[2.625rem] md:h-[2.875rem] p-2 md:p-[0.78125rem] md:pb-[0.84375rem] border rounded-xl text-black text-sm font-medium leading-4 md:leading-5 focus-visible:outline focus:outline-[#707C8F] ${
                addressError ? "border-[#D80027]" : "border-[#D1D5DB]"
              }`}
              placeholder="Enter your address"
              value={address}
              onChange={(e) => {
                setAddress((e.target as HTMLInputElement).value);
                setAddressError(""); // Clear error when user types
              }}
              onFocus={() => setIsAutocompleteOpen(true)}
              onBlur={() => {
                // Delay to allow for dropdown selection
                setTimeout(() => setIsAutocompleteOpen(false), 200);
              }}
            />
            {addressError && (
              <p className="text-[#D80027] text-xs sm:text-sm font-medium leading-5 mt-1 sm:mt-2 md:mt-[0.56rem]">
                {/* We don’t provide service at your ZIP code */}
                {addressError}
              </p>
            )}
          </div>
          <Button
            type="button"
            variant="primary"
            disabled={isLoading}
            onClick={handleCheckAvailability}
          >
            {isLoading ? (
              <div className="flex items-center gap-0.5 sm:gap-1">
                <Icons.Spinner className="w-4 h-4 animate-spin md:w-5 md:h-5" />
                <span>Verifying...</span>
              </div>
            ) : (
              "Continue"
            )}
          </Button>
        </DialogContent>
      </Dialog>
      <CongratulationsDialog
        showDialog={showCongratulationsDialog}
        setShowDialog={setShowCongratulationsDialog}
        isActivation={true}
        onContinue={onContinue}
        fullAddress={address}
        activationLoggedInCtaLabel="Activate fixed phone in SIM Service"
        onActivationLoggedInCta={handleNavigateToFixedPhoneSimService}
      />
      <ErrorDialog
        showDialog={showErrorDialog}
        setShowDialog={setShowErrorDialog}
      />
    </React.Fragment>
  );
};
